import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { SchoolDocumentsManager } from "components/admin";
import { useAuth } from "context/auth-context";
import { workspaceApi } from "pages/app/workspace/services";
import { SchoolTeamProfileTabProps } from "pages/app/workspace/school-team-profile/school-team-profile.types";
import { hasAnyPortalAction } from "utils/portal-access";

export const SchoolTeamDocumentsTab: React.FC<SchoolTeamProfileTabProps> = ({
  schoolId,
  team,
  teamId,
}) => {
  const { portalAccess } = useAuth();

  const documentsQuery = useQuery({
    queryKey: ["school-team-profile-documents", schoolId],
    queryFn: () => workspaceApi.schoolDocuments.list(schoolId),
    enabled: Boolean(schoolId),
  });
  const playersQuery = useQuery({
    queryKey: ["school-team-profile-player-pool", schoolId],
    queryFn: () => workspaceApi.schoolPlayers.list(schoolId),
    enabled: Boolean(schoolId),
  });
  const sportsQuery = useQuery({
    queryKey: ["school-team-profile-sports", schoolId],
    queryFn: () => workspaceApi.schoolSports.list(schoolId),
    enabled: Boolean(schoolId),
  });
  const teamPlayersQuery = useQuery({
    queryKey: ["school-team-profile-players", teamId],
    queryFn: () => workspaceApi.schoolTeamPlayers.list(teamId),
    enabled: Boolean(teamId),
  });

  const teamDocuments = useMemo(
    () =>
      (documentsQuery.data || []).filter(
        (document: any) => String(document.school_team_id || "") === String(teamId),
      ),
    [documentsQuery.data, teamId],
  );

  const canManageSchoolDocuments = hasAnyPortalAction(portalAccess, [
    "school_documents.manage",
    "school_documents.create",
    "school_documents.update",
    "school_documents.delete",
    "schools.update",
    "schools.manage",
    "school_matches.manage",
    "school_teams.update",
    "school_team_players.manage",
    "school_players.create",
  ]);
  const canVerifySchoolDocuments = hasAnyPortalAction(portalAccess, [
    "school_documents.verify",
    "school_documents.manage",
    "schools.update",
    "schools.manage",
    "school_matches.manage",
  ]);

  const documentInvalidateKeys = [
    ["school-team-profile-documents", schoolId],
    ["school-team-profile-dashboard", teamId],
    ["workspace-school-documents", schoolId],
    ["workspace-school-team-dashboard", teamId],
  ].filter((queryKey) => Boolean(queryKey[1]));

  return (
    <SchoolDocumentsManager
      canManage={canManageSchoolDocuments}
      canVerify={canVerifySchoolDocuments}
      createLabel="Upload team document"
      documents={teamDocuments}
      emptyDescription="Team documents will appear here once they are uploaded to the vault."
      invalidateKeys={documentInvalidateKeys}
      isLoading={documentsQuery.isLoading}
      onCreate={(payload) =>
        workspaceApi.schoolDocuments.create(schoolId, {
          ...payload,
          school_team_id: teamId,
        })
      }
      onRemove={(document) => workspaceApi.schoolDocuments.remove(String(document.id))}
      onUpdate={(document, payload) =>
        workspaceApi.schoolDocuments.update(String(document.id), {
          ...payload,
          school_team_id: teamId,
        })
      }
      onVerify={(document) => workspaceApi.schoolDocuments.verify(String(document.id))}
      players={playersQuery.data || teamPlayersQuery.data || []}
      sports={
        sportsQuery.data ||
        (team
          ? [
              {
                id: team.sport_id,
                name: team.sport_name,
              },
            ]
          : [])
      }
      subtitle="Keep scoresheets, medical packets, scouting notes, and fixture files tied directly to this team."
      teams={[team]}
      title="Team Documents"
    />
  );
};
